import { useState } from 'react';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';
import join from 'lodash/join';
import map from 'lodash/map';

import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import Checkbox from 'components/Checkbox';
import ColumnVisibilityModal from 'components/ColumnVisibilityModal';
import ExportButton from 'components/Export';
import KeyIndicatorsTable from 'modules/Distribution/components/KeyIndicators';
import {
  Title,
  HeaderRightComponents,
  KeyIndicatorsCardContainer
} from './container';

import { useUserMe } from 'apiTyped/hooks/users.query';
import { formatParam } from 'utils/formatParam';
import { useDistributionKpiDp } from 'apiTyped/hooks/distribution.query';
import {
  showDistributionKPDNBtg,
  showDistributionKPIConsoRate,
  showDistributionKPIBestVintage,
  showDistributionKPIOldDN
} from 'utils/personalisation';

// Helper function to process campaign comments
const formatCampaignComment = (comment) => {
  if (!comment || typeof comment !== 'string') {
    return '';
  }

  return comment
    .replace(/(\r\n|\n|\r)/gm, ' ')
    .replace(';', ',')
    .replace('1*', '★')
    .replace('2*', '★★')
    .replace('3*', '★★★');
};

const KeyIndicatorsCard = (props) => {
  const { currentCampaign, form, data } = props;

  const { user } = useUserMe();
  const isBranded = user.isBranded;
  const clientTags = user.client.tags || [];
  const [checkShowWines, setCheckShowWines] = useState(false);
  const [expandedTableLines, setExpandedTableLines] = useState({});
  const [hiddenColumns, setHiddenColumns] = useState([
    I18n.t('restaurants&caves.kpi.columns.averagePrice')
  ]);

  const filterData = () => {
    const {
      client_wines = [],
      rival_wines = [],
      client_marques = [],
      rival_marques = []
    } = data || {};
    const { wine = [], competitors = [] } = form;

    let dataWine = [...client_wines];
    let dataRival = [...rival_wines];
    let dataMarque = [...client_marques];
    let dataRivalMarque = [...rival_marques];
    const wineLabels = wine.map((item) => item.label);

    if (wine && wine.length > 0) {
      dataWine = !isBranded
        ? dataWine.filter((item) => wineLabels.includes(item.wine_name))
        : dataWine;
      dataMarque = dataMarque.filter((item) =>
        wineLabels.includes(item.marque_name)
      );
    }

    dataRival = !isBranded
      ? dataRival.filter((item) => competitors.includes(item.wine_name))
      : dataRival;

    dataRivalMarque = dataRivalMarque.filter((item) =>
      competitors.includes(item.marque_name)
    );

    return {
      ...data,
      client_wines: dataWine,
      rival_wines: dataRival,
      client_marques: dataMarque,
      rival_marques: dataRivalMarque
    };
  };

  const filteredData = filterData();
  const client_data = isBranded
    ? (filteredData.client_marques || []).map((item) =>
        get(item, 'marque_name')
      )
    : (filteredData.client_wines || []).map((item) => get(item, 'wine_name'));
  const rivals_data = isBranded
    ? (filteredData.rival_marques || []).map((item) => get(item, 'marque_name'))
    : (filteredData.rival_wines || []).map((item) => get(item, 'wine_name'));

  const campaignId = get(currentCampaign, 'id');
  const shouldFetch = campaignId !== undefined && client_data.length > 0;

  const { updatingKpiDp, kpiDp } = useDistributionKpiDp(
    {
      campaignId,
      client_data,
      rivals_data,
      isBranded
    },
    shouldFetch
  );

  const handleExpanded = (index) => {
    setExpandedTableLines((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleCheckbox = (lengthData) => {
    if (checkShowWines) {
      setCheckShowWines(false);
      setExpandedTableLines({});
    } else {
      const newExpandedTableLines = {};
      for (let index = 0; index < lengthData; index++) {
        newExpandedTableLines[index] = true;
      }
      setCheckShowWines(true);
      setExpandedTableLines(newExpandedTableLines);
    }
  };

  const formatItem = (item, kpiDpValue) => {
    item = { ...item };
    item.tp =
      kpiDpValue && (item.dn || item.dn === 0) && !updatingKpiDp
        ? Number((item.dn / kpiDpValue) * 100).toFixed(1) + '%'
        : ' - ';
    // based on dn_post_2005, as referenced in the API and the json translation file
    const dnOld = item.dn - item.dn_recent_vintage;
    item.dnOld = {
      value: dnOld,
      tooltip: `${dnOld} (${Number((dnOld / item.dn) * 100).toFixed(2)}%)`
    };

    item.vm = item.vm || item.vm === 0 ? Number(item.vm).toFixed(2) : ' - ';
    item.tf =
      item.tx_fidelite || item.tx_fidelite === 0
        ? Number(item.tx_fidelite * 100).toFixed(0) + '%'
        : '-';
    item.tc =
      item.tx_conso || item.tx_fidelite === 0
        ? Number(item.tx_conso * 100).toFixed(0) + '%'
        : '-';
    item.pmc =
      item.avg_price || item.avg_price === 0
        ? Number(item.avg_price).toLocaleString('fr-FR', {
            style: 'currency',
            currency: item.displayed_currency ? item.displayed_currency : 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })
        : '-';
    item.median_price_formatted =
      item.median_price || item.median_price === 0
        ? Number(item.median_price).toLocaleString('fr-FR', {
            style: 'currency',
            currency: item.displayed_currency ? item.displayed_currency : 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          })
        : '-';
    item.wine = item.marque_name ? item.marque_name : item.wine_name;

    return { ...item };
  };

  const formatData = (filteredData) => {
    const client_wines = filteredData.client_wines || [];
    const rival_wines = filteredData.rival_wines || [];
    const client_marques = filteredData.client_marques || [];
    const rival_marques = filteredData.rival_marques || [];
    let dataWines = [...client_wines].concat([...rival_wines]);

    let dataMarques = [...client_marques].concat([...rival_marques]);

    dataWines = dataWines.map((item) => formatItem(item, kpiDp));
    dataMarques = dataMarques.map((item) => formatItem(item, kpiDp));
    if (isBranded) {
      dataMarques = dataMarques.map((marque) => {
        const newItem = { ...marque };
        dataWines.forEach((wine) => {
          if (newItem.wines && newItem.wines.includes(wine.wine_name)) {
            if (!newItem.children) {
              newItem.children = [];
            }
            newItem.children.push(wine);
          }
        });
        if (newItem.children) {
          newItem.children.sort((a, b) =>
            (a.wine || '').localeCompare(b.wine || '')
          );
        }
        return newItem;
      });
      return dataMarques;
    }
    return dataWines;
  };

  const isSpirit = form.wineType === 'spirit';
  const isOnTradeMarket = form.currentNetworkCode === 'ON';
  const shouldDisplayGlassAvgPrice = isSpirit && isOnTradeMarket;
  const shouldDisplayBottleAvgPrice = !shouldDisplayGlassAvgPrice;

  const getColumnsNames = () => {
    return [
      I18n.t('restaurants&caves.kpi.columns.wine'),
      I18n.t('restaurants&caves.kpi.columns.dn'),
      I18n.t('restaurants&caves.kpi.columns.tp'),
      I18n.t('restaurants&caves.kpi.columns.vm'),
      I18n.t('restaurants&caves.kpi.columns.lostPov'),
      I18n.t('restaurants&caves.kpi.columns.wonPov'),
      I18n.t('restaurants&caves.kpi.columns.tf'),
      showDistributionKPIConsoRate(clientTags) &&
        I18n.t('restaurants&caves.kpi.columns.tc'),
      showDistributionKPIBestVintage(clientTags) &&
        I18n.t('restaurants&caves.kpi.columns.bestVintage'),
      showDistributionKPIOldDN(clientTags) &&
        I18n.t('restaurants&caves.kpi.columns.oldDn'),
      showDistributionKPDNBtg(clientTags) &&
        I18n.t('restaurants&caves.kpi.columns.dn_btg'),
      shouldDisplayBottleAvgPrice &&
        I18n.t('restaurants&caves.kpi.columns.averagePrice'),
      shouldDisplayBottleAvgPrice &&
        I18n.t('restaurants&caves.kpi.columns.medianPrice'),
      shouldDisplayGlassAvgPrice &&
        I18n.t('restaurants&caves.kpi.columns.glassAveragePrice')
    ].filter((item) => !!item);
  };

  if (!currentCampaign) {
    return null;
  }

  const formattedData = formatData(filteredData);
  const { wine, competitors } = form;

  const options = isBranded
    ? {
        brands: formatParam(JSON.stringify(wine.map((item) => item.label))),
        rivals: formatParam(JSON.stringify(competitors)),
        wine_type: form.wineType
      }
    : {
        wines: formatParam(JSON.stringify(wine.map((item) => item.label))),
        rivals: formatParam(JSON.stringify(competitors)),
        wine_type: form.wineType
      };

  return (
    <KeyIndicatorsCardContainer>
      <Card>
        <CardHeader
          styles={`.cardHeader{width:90%}`}
          title={
            <Title>
              <div className="titleContainer">
                <h1 className="title">
                  {I18n.t('restaurants&caves.kpi.title')}
                </h1>
              </div>

              <div className="leftText">
                <p>{formatCampaignComment(get(currentCampaign, 'comment'))}</p>
                <p>
                  {I18n.t('restaurants&caves.kpi.dpMessage')}:{' '}
                  {kpiDp ? kpiDp : 0}
                </p>
              </div>
            </Title>
          }
          fontSize="20px"
          rightComponents={
            <HeaderRightComponents>
              {isBranded && (
                <div className="containerCheckShowWines">
                  <Checkbox
                    checked={checkShowWines}
                    onChange={() => handleCheckbox(formattedData.length)}
                  />
                  <p>{I18n.t('showAllWines')}</p>
                </div>
              )}

              <ColumnVisibilityModal
                columnsNames={getColumnsNames()}
                hiddenColumns={hiddenColumns}
                onApply={(newHiddenColumns) =>
                  setHiddenColumns(newHiddenColumns)
                }
              />
              <ExportButton
                name={`${get(form, 'currentCampaign.network.name')}-${get(
                  form,
                  'currentCampaign.market.name'
                )}-${get(form, 'currentCampaign.year')}-${join(
                  map(get(form, 'wine'), 'label'),
                  '-'
                )}-${I18n.t('restaurants&caves.kpi.title')}`}
                path={`/distribution/${currentCampaign.id}/kpi/xls?`}
                options={options}
                xls
                newApi
              />
            </HeaderRightComponents>
          }
        />
        <div className="tableContainer">
          <KeyIndicatorsTable
            isBranded={isBranded}
            form={form}
            data={formattedData}
            kpiDp={kpiDp}
            tags_names={clientTags}
            hiddenColumns={hiddenColumns}
            expanded={expandedTableLines}
            handleExpanded={(value) => handleExpanded(value)}
          />
        </div>
      </Card>
    </KeyIndicatorsCardContainer>
  );
};

export default KeyIndicatorsCard;
